import { container } from "tsyringe";

import type Address from '@/Model/Address';
import type { IDetailViewInformation, IHistoryStateStackItem } from '#/CoreFrameInterfaces';
import type ViewFactory from '@/Factories/ViewFactory';

export interface INavigationService {
    historyStateStack: IHistoryStateStackItem[];
    detailViewStack: KnockoutComputed<IDetailViewInformation[]>;

    /**
     * Fügt Informationen zum aktuellen History-State hinzu
     * Diese Funktion ist rein additiv, bestehende Werte nicht überschrieben!
     * @param additionalInformation
     */
    addInformationToCurrentState(additionalInformation: { [key: string]: any }): void;

    /**
     * Gibt zusätzliche Informationen zum aktuellen History-State zurück.
     */
    getInformationFromCurrentState(key: string): any;

    getLastHistoryStateStackItem(): IHistoryStateStackItem;

    /**
     * Versucht eine zuletzt aufgerufene Factory anhand des Namen zu finden,
     */
    findLastFactory(name): ViewFactory;

    isNavigatingBack(): boolean;

    /**
     * Setzt die aktuelle Navigation auf eine Rücknavigation
     * Damit kann nicht nur die Vorwärtsnavigation sondern auch die Rücknavigation von außen getriggert werden
     * z.B. Custom Logik im Abbrechen Fall
     */
    setBackNavigation(): void;

    /**
     * Liefert die derzeitig öffentlich sichtbare Adresse
     * (enthält nicht die ggf. versteckten Parameter)
     */
    getAddress(): Address;

    getInitialAddress(): Address;

    /**
     * Setzt / Ändert einen Hidden-Parameter der aktuellen Adresse
     */
    setHiddenParameterToAddress(contextIdentifier: string, parameterName: string, value: any): void;

    /**
     * Setzt / Ändert einen Parameter der aktuellen Adresse
     * @param contextIdentifier
     * @param parameterName
     * @param value
     */
    setParameterToAddress(contextIdentifier: string, parameterName: string, value: string): void;

    /**
     * Convenience-Funktion, um eine Menge an Parametern der aktuellen Adresse zu setzen
     * @param contextIdentifier
     * @param parameters
     * @param removeOther
     */
    setParametersToAdress(contextIdentifier: string, parameters: { [key: string]: string }, removeOther: boolean): void;

    /**
     * Liefert die letzte zur Navigation verwendete Addresse
     * (inklusive ggf. versteckter Parameter)
     */
    getStatefulAddress(): Address;

    /**
     * Sucht die zu einem DOM-Element gehörende ViewFactory
     */
    findFactory(element: JQuery): ViewFactory;

    /**
     * Läd eine ViewFactory anhand ihres Namen
     */
    loadViewFactory(name: string, params?, tryToReuse?: boolean, address?: Address, disposeOther?: boolean): ViewFactory;

    /**
     * Liefert den Identifier der sichtbaren ViewFactory
     */
    getVisibleViewIdentifier(): string;

    /**
     * Entfernt eine ViewFactory.
     */
    removeViewFactory(viewFactory: ViewFactory): void;

    /**
     * Liefert die Anzahl der aktuell vorhandenen modalen ViewFactories.
     */
    getModalViewFactories(): number;

    /**
     * Lädt eine ViewFactory isoliert, d.h. ohne Abhängigkeiten zu setzen oder Registrierungen durchzuführen.
     */
    loadViewFactoryIsolated<T extends ViewFactory>(name: string, params, renderView: boolean): T;

    /**
     * Löscht eine ViewFactory ohne ggf. abhängige Registrierungen o.Ä. zu beachten.
     */
    removeViewFactoryIsolated(viewFactory: ViewFactory): void;

    addToRegion(region: string, $element: JQuery): void;

    /**
     * Säubert eine Region
     */
    cleanRegion(name: string): void;

    /**
     * Navigiert zur vorherigen Seite zurück
     */
    navigateBack(onNavigated?: () => void): void;

    forceNavigateBack(onNavigated?: () => void): void;

    /**
     * Aktualisiert die aktuelle ViewFactory
     */
    refresh(): void;

    continueNavigation(navigationMessage: {
        address: Address,
        pushState: any,
        tryToReuse: boolean,
        isNavigatingBack: boolean,
        isDisposed: boolean
    }): void;

    /**
     * Startet die Navigation zu einer Adresse
     */
    navigateToAddress(address, pushState?, sendMessage?: boolean, tryToReuse?: boolean, openInNewWindow?: boolean): boolean;

    /**
     * Benachrichtigt über das Zurücksetzen der Navigation.
     * @param {boolean} keepNavigationHistory - Optionaler Parameter, der angibt, ob die Navigation für den aktuellen Zustand beibehalten werden soll.
     * @return {void}
     */
    notifyNavigationReset(keepNavigationHistory?: boolean): void;

    /**
     * Navigiert zu der zuletzt weiterleitenden Adresse
     */
    navigateToRedirectingAddress(): void;

    /**
     * Startet die Navigation zu einer URL
     */
    navigateToUrl(url: string, onNavigated?: () => void, pushState?, sendMessage?: boolean, tryToReuse?: boolean, openInNewWindow?: boolean): void;

    /**
     * Verarbeitet eine CoreFrame-URL und führt die hinterlegte Method aus
     */
    handleCoreframeUrl(url: string): void;

    /**
     * Startet die Navigation zum Login
     */
    navigateToLogin(): void;

    handleUserInputAfterLogin(changePassword: boolean, needsTwoFaRegistration: boolean): void;

    navigateToChangePassword(allowCloseWindow?: boolean, callback?: () => void): void;

    navigateToTwoFAInitialConfig(): void;

    navigateToTwoFA(): void;

    /**
     * Startet die Navigation zum Logout
     */
    navigateToLogout(): void;

    /**
     * Startet die Navigation zur initial aufgerufenen Adresse
     */
    navigateToStart(): void;

    /**
     * Startet die Navigation zur eingerichteten Startseite
     */
    navigateToHome(openInNewWindow?: boolean): void;

    /**
     * Läd die Basis-Seite der SPA neu
     * @param doLogout Gibt an, ob ein Logout stattfinden soll
     */
    reload(doLogout?: boolean, goToStartpage?: boolean): void;

    reloadAndLogout(): void;

    /**
     * Initialisiert den NavigationService und leitet initial an das Login-Modul weiter
     * Registriert eine Funktion an den Browserevents "Vor" und "Zurück" um vorherigen Zustand wiederherstellen zu können
     */
    init(): void;
}

export default class NavigationService {
    public static getInstance(): INavigationService {
        return container.resolve<INavigationService>("NavigationService");
    }
}